import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  addFavorite,
  getExam,
  getExamRecord,
  getFavorites,
  getLatestRecordForExam,
  removeFavorite,
  submitExam,
  updateExamTitle,
} from '../../../services/examService';
import { createExamGenSession, verifyExamGenToken, verifyExamPassword } from '../../../services/settingsService';
import { Eye, EyeOff } from 'lucide-react';
import styles from './ExamPage.module.css';

export default function ExamPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const recordId = searchParams.get('recordId');
  const mode = searchParams.get('mode');
  const navigate = useNavigate();
  // const { t } = useLanguage();

  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const [favoriteBusy, setFavoriteBusy] = useState({});
  const [showRetakeModal, setShowRetakeModal] = useState(false);
  const [retakePassword, setRetakePassword] = useState('');
  const [showRetakePassword, setShowRetakePassword] = useState(false);
  const [retakePasswordError, setRetakePasswordError] = useState('');
  const [retakeVerifying, setRetakeVerifying] = useState(false);
  const retakePwdInputRef = useRef(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isExamFullscreen, setIsExamFullscreen] = useState(false);

  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [titleSaveError, setTitleSaveError] = useState('');
  const titleInputRef = useRef(null);

  // Title auth (reuses exam-gen session token, 5-min IP-bound)
  const [titleAuthToken, setTitleAuthToken] = useState(() => {
    const stored = sessionStorage.getItem('titleEditToken');
    const expiry = sessionStorage.getItem('titleEditTokenExpiry');
    if (stored && expiry && Date.now() < Number(expiry)) return stored;
    return null;
  });
  const [showTitleAuthModal, setShowTitleAuthModal] = useState(false);
  const [showTitleAuthEye, setShowTitleAuthEye] = useState(false);
  const [titleAuthPassword, setTitleAuthPassword] = useState('');
  const [titleAuthError, setTitleAuthError] = useState('');
  const [titleAuthVerifying, setTitleAuthVerifying] = useState(false);
  const titleAuthInputRef = useRef(null);

  const timerRef = useRef(null);
  const examPaperRef = useRef(null);
  const scoreBannerRef = useRef(null);
  const submittedRecordIdRef = useRef(null);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  useEffect(() => {
    if (showTitleAuthModal && titleAuthInputRef.current) {
      titleAuthInputRef.current.focus();
    }
  }, [showTitleAuthModal]);

  const handleStartEditTitle = () => {
    if (!exam) return;
    setEditedTitle(exam.title || '');
    setTitleSaveError('');
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setTitleSaveError('');
    setEditedTitle(exam?.title || '');
  };

  // Core save logic – called after auth is confirmed.
  const doSaveTitle = async (token) => {
    if (!exam) return;
    const trimmedTitle = editedTitle.trim();
    if (!trimmedTitle) { setTitleSaveError('Title cannot be empty'); return; }

    const targetExamId = exam.id || id;
    setIsSavingTitle(true);
    setTitleSaveError('');

    try {
      await updateExamTitle(targetExamId, trimmedTitle, token);
      setExam(prev => (prev ? { ...prev, title: trimmedTitle } : prev));

      try {
        const stored = sessionStorage.getItem(`exam-${targetExamId}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          parsed.title = trimmedTitle;
          sessionStorage.setItem(`exam-${targetExamId}`, JSON.stringify(parsed));
        }
      } catch (e) { console.error(e); }

      try {
        const subject = exam.subject || 'spanish';
        const cacheKey = `exam-history-${subject}`;
        const cachedHistoryStr = localStorage.getItem(cacheKey);
        if (cachedHistoryStr) {
          const cachedHistory = JSON.parse(cachedHistoryStr);
          const updatedHistory = cachedHistory.map(item => {
            if (item.examId === targetExamId || (item.exam && item.exam.id === targetExamId)) {
              return { ...item, exam: { ...item.exam, title: trimmedTitle } };
            }
            return item;
          });
          localStorage.setItem(cacheKey, JSON.stringify(updatedHistory));
        }
      } catch (e) { console.error(e); }

      setIsEditingTitle(false);
    } catch (err) {
      console.error(err);
      const serverMsg = err?.response?.data?.error;
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        // Token expired – clear it and ask for password again
        setTitleAuthToken(null);
        sessionStorage.removeItem('titleEditToken');
        sessionStorage.removeItem('titleEditTokenExpiry');
        setTitleSaveError('Session expired. Please enter the password again.');
        setShowTitleAuthEye(false);
        setShowTitleAuthModal(true);
      } else {
        setTitleSaveError(serverMsg || 'Failed to update title');
      }
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!exam) return;
    const trimmedTitle = editedTitle.trim();
    if (!trimmedTitle) { setTitleSaveError('Title cannot be empty'); return; }
    if (trimmedTitle === exam.title) { setIsEditingTitle(false); return; }

    // Check cached token validity
    const cachedToken = titleAuthToken;
    const cachedExpiry = sessionStorage.getItem('titleEditTokenExpiry');
    const tokenValid = cachedToken && cachedExpiry && Date.now() < Number(cachedExpiry);

    if (tokenValid) {
      // Optionally double-check with server (lightweight)
      const stillValid = await verifyExamGenToken(cachedToken).catch(() => false);
      if (stillValid) {
        await doSaveTitle(cachedToken);
        return;
      }
      // Token revoked server-side – clear it
      setTitleAuthToken(null);
      sessionStorage.removeItem('titleEditToken');
      sessionStorage.removeItem('titleEditTokenExpiry');
    }

    // No valid token – prompt for password
    setTitleAuthPassword('');
    setTitleAuthError('');
    setShowTitleAuthEye(false);
    setShowTitleAuthModal(true);
  };

  const handleTitleAuthSubmit = async () => {
    if (titleAuthPassword.length !== 6) {
      setTitleAuthError('Please enter the 6-digit password.');
      return;
    }
    setTitleAuthVerifying(true);
    setTitleAuthError('');
    try {
      const result = await createExamGenSession(titleAuthPassword.trim());
      if (!result.valid) {
        setTitleAuthError(result.error || 'Incorrect password.');
        return;
      }
      // Store IP-bound token (5 min)
      const expiresAt = Date.now() + (result.expiresInMs || 5 * 60 * 1000);
      setTitleAuthToken(result.token);
      sessionStorage.setItem('titleEditToken', result.token);
      sessionStorage.setItem('titleEditTokenExpiry', String(expiresAt));
      setShowTitleAuthModal(false);
      setTitleAuthPassword('');
      await doSaveTitle(result.token);
    } catch (err) {
      setTitleAuthError('Unable to verify password. Please try again.');
    } finally {
      setTitleAuthVerifying(false);
    }
  };

  const isMcOptionCorrect = (q, letter, optText) => {
    if (!q || !q.answer) return false;
    const ca = q.answer.trim().toLowerCase();
    const l = letter.trim().toLowerCase();
    if (ca === l) return true;
    const caLetterMatch = ca.match(/^([a-d])[\s).:]/i);
    if (caLetterMatch && caLetterMatch[1].toLowerCase() === l) return true;
    const cleanOpt = (optText || '').replace(/^[a-d][\s).:]\s*/i, '').trim().toLowerCase();
    return ca === cleanOpt;
  };

  const gradeQuestionLocally = (q, userAns) => {
    if (!userAns || !q) return false;
    const ua = userAns.toString().trim().toLowerCase();
    const ca = (q.answer || "").toString().trim().toLowerCase();

    const isMc = q.type === "mc" || q.type === "multipleChoice" || (Array.isArray(q.options) && q.options.length > 2);
    const isTf = q.type === "tf" || q.type === "trueFalse" || (Array.isArray(q.options) && q.options.length === 2);

    if (isMc) {
      if (ua === ca) return true;
      const caLetterMatch = ca.match(/^([a-d])[\s).:]/i);
      if (caLetterMatch && ua === caLetterMatch[1].toLowerCase()) return true;
      if (Array.isArray(q.options)) {
        const userOptIdx = q.options.findIndex((opt, idx) => {
          const letter = String.fromCharCode(65 + idx).toLowerCase();
          const cleanOpt = opt.replace(/^[a-d][\s).:]\s*/i, '').trim().toLowerCase();
          return ua === letter || ua === cleanOpt;
        });
        const correctOptIdx = q.options.findIndex((opt, idx) => {
          const letter = String.fromCharCode(65 + idx).toLowerCase();
          const cleanOpt = opt.replace(/^[a-d][\s).:]\s*/i, '').trim().toLowerCase();
          return ca === letter || ca === cleanOpt || (caLetterMatch && caLetterMatch[1].toLowerCase() === letter);
        });
        if (userOptIdx !== -1 && userOptIdx === correctOptIdx) return true;
      }
      return false;
    }
    if (isTf) {
      return ua === ca || (ua === "true" && ca === "true") || (ua === "false" && ca === "false") || (ua === "t" && ca === "true") || (ua === "f" && ca === "false");
    }

    const norm = (str) => {
      return str
        .replace(/[''`´]/g, "'")
        .replace(/[.,!?¡¿""":;]/g, " ")
        .replace(/\bi'm\b/g, "i am")
        .replace(/\bhe's\b/g, "he is")
        .replace(/\bshe's\b/g, "she is")
        .replace(/\bdon't\b/g, "do not")
        .replace(/\bdoesn't\b/g, "does not")
        .replace(/\bdidn't\b/g, "did not")
        .replace(/\bcan't\b/g, "cannot")
        .replace(/\s+/g, " ")
        .trim();
    };

    const normUa = norm(ua);
    const normCa = norm(ca);
    if (normUa === normCa) return true;

    const normPronUa = normUa.replace(/\b(he|she|it|you)\b/g, "_PRON_");
    const normPronCa = normCa.replace(/\b(he|she|it|you)\b/g, "_PRON_");
    if (normPronUa && normPronUa === normPronCa) return true;

    return (normCa && normUa.includes(normCa)) || (normUa && normCa.includes(normUa));
  };

  useEffect(() => {
    async function loadData() {
      if (recordId && submittedRecordIdRef.current === String(recordId)) {
        return;
      }
      setLoading(true);
      clearInterval(timerRef.current);
      try {
        if (recordId) {
          // Review mode — load historical record
          const record = await getExamRecord(recordId);
          if (record && record.exam) {
            setExam(record.exam);
            const savedAnswers = JSON.parse(record.answersJson || '{}');
            setAnswers(savedAnswers);

            const details = savedAnswers._details || {};
            const allQs = Object.values(record.exam.sections || {}).flat();
            allQs.forEach(q => {
              if (details[q.id] === undefined) {
                details[q.id] = gradeQuestionLocally(q, savedAnswers[q.id]);
              }
            });

            setResult({
              score: record.score,
              total: record.total,
              details,
              isReview: true
            });
            setTimeElapsed(record.timeElapsed || 0);
            try {
              const favorites = await getFavorites(record.exam.subject);
              setFavoriteIds(new Set((favorites || []).map(fav => fav.id)));
            } catch (e) {
              console.error(e);
            }
          } else {
            navigate('/study/spanish');
          }
        } else if (mode === 'retake') {
          // Explicit retake mode authorized via retake password modal
          setAnswers({});
          setResult(null);
          setSubmitting(false);
          setTimeElapsed(0);
          setFavoriteIds(new Set());
          setFavoriteBusy({});

          const examData = await getExam(id);
          if (examData) {
            setExam(examData);
            timerRef.current = setInterval(() => {
              setTimeElapsed(prev => prev + 1);
            }, 1000);
            try {
              const favorites = await getFavorites(examData.subject);
              setFavoriteIds(new Set((favorites || []).map(fav => fav.id)));
            } catch (e) {
              console.error(e);
            }
          } else {
            navigate('/study/spanish');
          }
        } else {
          // No recordId and not explicitly retaking:
          // Check if this exam has already been completed before!
          const latestRecord = await getLatestRecordForExam(id);
          if (latestRecord && latestRecord.id) {
            navigate(`/study/spanish/exam/${id}?recordId=${latestRecord.id}`, { replace: true });
            return;
          }

          // Fresh first-time take
          setAnswers({});
          setResult(null);
          setSubmitting(false);
          setTimeElapsed(0);
          setFavoriteIds(new Set());
          setFavoriteBusy({});

          const examData = await getExam(id);
          if (examData) {
            setExam(examData);
            // restart timer
            timerRef.current = setInterval(() => {
              setTimeElapsed(prev => prev + 1);
            }, 1000);
            try {
              const favorites = await getFavorites(examData.subject);
              setFavoriteIds(new Set((favorites || []).map(fav => fav.id)));
            } catch (e) {
              console.error(e);
            }
          } else {
            navigate('/study/spanish');
          }
        }
      } catch (err) {
        console.error(err);
        navigate('/study/spanish');
      } finally {
        setLoading(false);
      }
    }
    loadData();

    return () => clearInterval(timerRef.current);
  }, [id, recordId, mode, navigate]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsExamFullscreen(document.fullscreenElement === examPaperRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const sectionKeys = Object.keys(exam?.sections || {});
    if (!sectionKeys.length) return undefined;

    setActiveSection(prev => (sectionKeys.includes(prev) ? prev : sectionKeys[0]));

    const updateActivePageSection = () => {
      if (document.fullscreenElement === examPaperRef.current) return;

      const threshold = 140;
      let currentSection = sectionKeys[0];

      sectionKeys.forEach((key) => {
        const sectionEl = document.getElementById(`page-section-${key}`);
        if (sectionEl && sectionEl.getBoundingClientRect().top <= threshold) {
          currentSection = key;
        }
      });

      setActiveSection(prev => (prev === currentSection ? prev : currentSection));
    };

    updateActivePageSection();
    const showcaseContent = document.getElementById('showcase-content');
    window.addEventListener('scroll', updateActivePageSection, { passive: true });
    window.addEventListener('resize', updateActivePageSection);
    showcaseContent?.addEventListener('scroll', updateActivePageSection, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateActivePageSection);
      window.removeEventListener('resize', updateActivePageSection);
      showcaseContent?.removeEventListener('scroll', updateActivePageSection);
    };
  }, [exam]);

  useEffect(() => {
    if (result && !result.isReview) {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => { });
      }
      const scrollTimer = setTimeout(() => {
        if (scoreBannerRef.current) {
          scoreBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        const showcaseContent = document.getElementById('showcase-content');
        if (showcaseContent) {
          showcaseContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);

      return () => clearTimeout(scrollTimer);
    }
  }, [result]);

  const handleOptionChange = (questionId, value) => {
    if (result) return; // Prevent changes after submit
    setAnswers(prev => {
      const next = { ...prev, [questionId]: value };
      answersRef.current = next;
      return next;
    });
  };

  const confirmSubmit = async () => {
    setShowSubmitModal(false);
    setSubmitting(true);
    clearInterval(timerRef.current);
    const latestAnswers = answersRef.current;
    try {
      const res = await submitExam(id, latestAnswers, timeElapsed);

      const allQs = Object.values(exam?.sections || {}).flat();
      let details = res?.details || {};
      let earned = 0;
      let totalPts = 0;

      allQs.forEach(q => {
        const pts = q.points || 1;
        totalPts += pts;
        const isOk = (details[q.id] !== undefined)
          ? details[q.id]
          : gradeQuestionLocally(q, latestAnswers[q.id]);
        details[q.id] = isOk;
        if (isOk) earned += pts;
      });

      const score = res?.score ?? (totalPts > 0 ? Math.round((earned / totalPts) * 100) : 0);

      setResult({
        score,
        total: 100,
        details,
        message: res?.message || 'Submitted successfully!',
        isReview: true
      });

      if (res?.recordId) {
        submittedRecordIdRef.current = String(res.recordId);
        navigate(`/study/spanish/exam/${exam?.id || id}?recordId=${res.recordId}`, { replace: true });
      }
    } catch (err) {
      console.error(err);
      alert("Failed to submit exam.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    setRetakePassword('');
    setShowRetakePassword(false);
    setRetakePasswordError('');
    setShowRetakeModal(true);
  };

  useEffect(() => {
    if (showRetakeModal && retakePwdInputRef.current) {
      retakePwdInputRef.current.focus();
    }
  }, [showRetakeModal]);

  const confirmRetake = async () => {
    if (retakePassword.length !== 6 || retakeVerifying) return;
    setRetakeVerifying(true);
    setRetakePasswordError('');

    try {
      const isValid = await verifyExamPassword(retakePassword);
      if (!isValid) {
        setRetakePasswordError('Incorrect password. Please try again.');
        setRetakeVerifying(false);
        return;
      }
    } catch (err) {
      setRetakePasswordError('Failed to verify password. Please try again.');
      setRetakeVerifying(false);
      return;
    }

    setRetakeVerifying(false);
    setShowRetakeModal(false);
    // Clear all previous session state immediately
    clearInterval(timerRef.current);
    setAnswers({});
    setResult(null);
    setSubmitting(false);
    setTimeElapsed(0);
    setFavoriteIds(new Set());
    setFavoriteBusy({});

    navigate(`/study/spanish/exam/${exam?.id || id}?mode=retake`, { replace: true });
  };

  const handleFavoriteToggle = async (question) => {
    if (favoriteBusy[question.id]) return;

    const isFavorite = favoriteIds.has(question.id);
    setFavoriteBusy(prev => ({ ...prev, [question.id]: true }));
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (isFavorite) next.delete(question.id);
      else next.add(question.id);
      return next;
    });

    try {
      if (isFavorite) {
        await removeFavorite(question.id);
      } else {
        await addFavorite(question.id, question, exam?.subject);
      }
    } catch (err) {
      console.error(err);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (isFavorite) next.add(question.id);
        else next.delete(question.id);
        return next;
      });
      alert("Failed to update favorite.");
    } finally {
      setFavoriteBusy(prev => {
        const next = { ...prev };
        delete next[question.id];
        return next;
      });
    }
  };

  const handleSubmit = () => {
    setShowSubmitModal(true);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const toggleFullscreen = async () => {
    const target = examPaperRef.current;
    if (!target) return;

    if (document.fullscreenElement === target) {
      await document.exitFullscreen();
      setIsExamFullscreen(false);
      return;
    }

    if (target.requestFullscreen) {
      await target.requestFullscreen();
      setIsExamFullscreen(true);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <span className="spinner" />
        <p>Loading Exam...</p>
      </div>
    );
  }

  if (!exam) return null;

  const sections = exam.sections || {};
  const allQuestions = Object.values(sections).flat();
  const answeredCount = allQuestions.filter(q => {
    const answer = answers[q.id];
    return typeof answer === 'string' ? answer.trim() !== '' : Boolean(answer);
  }).length;
  const totalQuestions = allQuestions.length;
  const sectionIcons = {
    multipleChoice: '☑️',
    fillInTheBlank: '✏️',
    trueFalse: '⚖️',
    translation: '🌐',
  };

  const getSectionTitle = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

  const updateActiveSection = (idPrefix, threshold = 140) => {
    const sectionKeys = Object.keys(sections);
    if (!sectionKeys.length) return;

    let currentSection = sectionKeys[0];
    sectionKeys.forEach((key) => {
      const sectionEl = document.getElementById(`${idPrefix}-section-${key}`);
      if (sectionEl && sectionEl.getBoundingClientRect().top <= threshold) {
        currentSection = key;
      }
    });

    setActiveSection(prev => (prev === currentSection ? prev : currentSection));
  };

  const renderExamLayout = (idPrefix, isFullscreen = false) => (
    <div className={`${styles.examLayout} ${isFullscreen ? styles.fullscreenExamLayout : ''}`}>
      <nav className={styles.sideNav}>
        <div className={styles.sideNavTitle}>Sections</div>
        {Object.keys(sections).map(key => {
          const sectionQuestions = sections[key];
          const sectionScore = result
            ? sectionQuestions.reduce((acc, q) => {
              const isOk = (result.details && result.details[q.id] !== undefined)
                ? result.details[q.id]
                : gradeQuestionLocally(q, answers[q.id]);
              return acc + (isOk ? 1 : 0);
            }, 0)
            : null;
          const icon = sectionIcons[key] || '📄';

          return (
            <button key={key} className={`${styles.navItem} ${activeSection === key ? styles.navItemActive : ''}`} onClick={() => {
              setActiveSection(key);
              document.getElementById(`${idPrefix}-section-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}>
              <span className={styles.navIcon}>{icon}</span>
              <span className={styles.navLabel}>{getSectionTitle(key)}</span>
              <span className={styles.navBadge}>
                {result ? `${sectionScore}/${sectionQuestions.length}` : `${sectionQuestions.length}`}
              </span>
            </button>
          );
        })}
      </nav>

      <div
        className={styles.examContent}
        onScroll={() => updateActiveSection(idPrefix, isFullscreen ? 170 : 140)}
      >
        {Object.entries(sections).map(([sectionKey, questions]) => {
          const icon = sectionIcons[sectionKey] || '📄';

          return (
            <div key={sectionKey} id={`${idPrefix}-section-${sectionKey}`} className={styles.section}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTitleRow}>
                  <span className={styles.sectionIconBig}>{icon}</span>
                  <h2 className={styles.sectionTitle}>{getSectionTitle(sectionKey)}</h2>
                  <span className={styles.sectionCount}>
                    {questions.length} questions {questions.length > 0 && `(${questions[0].points} pts each)`}
                  </span>
                </div>
              </div>

              <div className={styles.questions}>
                {questions.map((q, i) => {
                  const userAnswer = answers[q.id] || '';
                  const isChecked = result
                    ? ((result.details && result.details[q.id] !== undefined)
                      ? result.details[q.id]
                      : gradeQuestionLocally(q, userAnswer))
                    : null;

                  let cardClass = styles.questionCard;
                  if (result) {
                    cardClass += isChecked ? ` ${styles.questionCorrect}` : ` ${styles.questionWrong}`;
                  }

                  return (
                    <div key={q.id} className={cardClass}>
                      <div className={styles.questionTitleWrap}>
                        <span className={styles.questionTextInline}>
                          <strong className={styles.questionNumber}>Q{i + 1}.</strong>
                          {q.question}
                        </span>
                        <button
                          type="button"
                          className={`${styles.favBtn} ${favoriteIds.has(q.id) ? styles.favBtnActive : ''}`}
                          onClick={() => handleFavoriteToggle(q)}
                          disabled={!!favoriteBusy[q.id]}
                          aria-label={favoriteIds.has(q.id) ? 'Remove from favorites' : 'Add to favorites'}
                          title={favoriteIds.has(q.id) ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          {favoriteIds.has(q.id) ? '★' : '☆'}
                        </button>
                      </div>

                      {(q.type === 'mc' || q.type === 'multipleChoice' || (Array.isArray(q.options) && q.options.length > 2)) && (
                        <div className={styles.optionsGrid}>
                          {q.options.map((opt, optIdx) => {
                            const letter = String.fromCharCode(65 + optIdx);
                            const isSelected = userAnswer === letter;
                            let optClass = styles.optionBtn;
                            if (isSelected) optClass += ` ${styles.optionSelected}`;

                            if (result) {
                              const isCorrectAnswer = isMcOptionCorrect(q, letter, opt);
                              if (isCorrectAnswer) optClass += ` ${styles.optionCorrect}`;
                              else if (isSelected && !isCorrectAnswer) optClass += ` ${styles.optionWrong}`;
                            }

                            return (
                              <button
                                key={letter}
                                className={optClass}
                                onClick={() => handleOptionChange(q.id, letter)}
                                disabled={!!result}
                              >
                                <span className={styles.optionLetter}>{letter}</span>
                                <span>{opt.replace(/^[A-D][.)]\s*/i, '')}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {q.type === 'tf' && (
                        <div className={styles.optionsGrid}>
                          {['True', 'False'].map((opt) => {
                            const isSelected = userAnswer === opt;
                            let optClass = styles.optionBtn;
                            if (isSelected) optClass += ` ${styles.optionSelected}`;

                            if (result) {
                              const isCorrectAnswer = q.answer === opt;
                              if (isCorrectAnswer) optClass += ` ${styles.optionCorrect}`;
                              else if (isSelected && !isCorrectAnswer) optClass += ` ${styles.optionWrong}`;
                            }

                            return (
                              <button
                                key={opt}
                                className={optClass}
                                onClick={() => handleOptionChange(q.id, opt)}
                                disabled={!!result}
                              >
                                <span className={styles.optionLetter}>{opt.charAt(0)}</span>
                                <span>{opt}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {(q.type === 'fill' || q.type === 'translation') && (
                        <div className={styles.fillWrap}>
                          <textarea
                            className={`${styles.fillInput} ${styles.writingAnswer} ${result ? (isChecked ? styles.inputCorrect : styles.inputWrong) : ''}`}
                            value={userAnswer}
                            onChange={(e) => handleOptionChange(q.id, e.target.value)}
                            disabled={!!result}
                            placeholder="Type your answer here..."
                            rows={1.2}
                          />
                          {result && !isChecked && (
                            <div className={styles.correctAnswerBelow}>
                              <span className={styles.correctAnswerLabel}>Correct answer:</span>
                              <span className={styles.correctAnswerText}>{q.answer}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );




  // Determine portal target: use fullscreen element when in fullscreen, otherwise document.body
  const modalPortalTarget = isExamFullscreen ? examPaperRef.current : (typeof document !== 'undefined' ? document.body : null);

  const retakeModalJsx = showRetakeModal ? (
    <div className={`${styles.retakeModalOverlay} ${isExamFullscreen ? styles.retakeModalOverlayFullscreen : ''}`} role="dialog" aria-modal="true" aria-labelledby="retake-modal-title">
      <div className={`${styles.retakeModal} ${retakePasswordError ? styles.pinShakeAnim : ''}`}>
        <div className={styles.retakeModalIcon}>↺</div>
        <h3 id="retake-modal-title" className={styles.retakeModalTitle}>
          Reset this exam?
        </h3>
        <p className={styles.retakeModalText}>
          Enter the 6-digit exam password to retake this exam. Your current answers and result view will be cleared.
        </p>

        {/* 6-Digit PIN input in theme green */}
        <div className={styles.pinInputWrapper}>
          <div
            className={styles.pinBoxesRow}
            onClick={() => retakePwdInputRef.current?.focus()}
          >
            <input
              ref={retakePwdInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={retakePassword}
              onChange={(e) => {
                setRetakePassword(e.target.value.replace(/\D/g, ''));
                setRetakePasswordError('');
              }}
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  retakePassword.length === 6 &&
                  !retakeVerifying
                ) {
                  confirmRetake();
                }
              }}
              className={styles.hiddenInputOverlay}
              autoFocus
            />

            {Array.from({ length: 6 }).map((_, idx) => {
              const isFilled = idx < retakePassword.length;
              const isActive = idx === retakePassword.length;
              const digit = retakePassword[idx];

              return (
                <div
                  key={idx}
                  className={`${styles.pinBox} ${
                    isFilled ? styles.pinBoxFilled : ''
                  } ${isActive ? styles.pinBoxActive : ''} ${
                    retakePasswordError ? styles.pinBoxError : ''
                  }`}
                >
                  {isFilled ? (
                    showRetakePassword ? (
                      <span
                        key={`digit-${idx}-${digit}`}
                        className={styles.pinDigit}
                      >
                        {digit}
                      </span>
                    ) : (
                      <span
                        key={`bullet-${idx}`}
                        className={styles.pinBullet}
                      />
                    )
                  ) : isActive ? (
                    <span className={styles.pinBlinkingCursor} />
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Borderless Eye toggle button */}
          <button
            type="button"
            className={styles.pinEyeBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowRetakePassword((prev) => !prev);
            }}
            title={showRetakePassword ? 'Hide password' : 'Show password'}
            aria-label={showRetakePassword ? 'Hide password' : 'Show password'}
          >
            {showRetakePassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        {retakePasswordError && (
          <p className={styles.retakePwdError}>{retakePasswordError}</p>
        )}

        <div className={styles.retakeModalActions}>
          <button
            className={styles.retakeModalCancel}
            onClick={() => setShowRetakeModal(false)}
            disabled={retakeVerifying}
          >
            Cancel
          </button>
          <button
            className={styles.retakeModalConfirm}
            onClick={confirmRetake}
            disabled={retakePassword.length !== 6 || retakeVerifying}
          >
            {retakeVerifying ? 'Verifying...' : 'Retake'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const submitModalJsx = showSubmitModal ? (
    <div className={`${styles.retakeModalOverlay} ${isExamFullscreen ? styles.retakeModalOverlayFullscreen : ''}`} role="dialog" aria-modal="true" aria-labelledby="submit-modal-title">
      <div className={styles.retakeModal}>
        <div className={styles.retakeModalIcon}>✓</div>
        <h3 id="submit-modal-title" className={styles.retakeModalTitle}>
          Submit this exam?
        </h3>
        <p className={styles.retakeModalText}>
          You have answered {answeredCount} of {totalQuestions} questions. After submitting, your answers will be graded and locked.
        </p>
        <div className={styles.retakeModalActions}>
          <button className={styles.retakeModalCancel} onClick={() => setShowSubmitModal(false)}>
            Cancel
          </button>
          <button className={styles.retakeModalConfirm} onClick={confirmSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const gradingModalJsx = submitting ? (
    <div
      className={`${styles.gradingOverlay} ${isExamFullscreen ? styles.gradingOverlayFullscreen : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Grading exam"
    >
      <div className={styles.gradingCard}>
        {/* Animated Radar Pulse Icon */}
        <div className={styles.gradingIconWrapper}>
          <div className={styles.gradingPulseRing} />
          <div className={styles.gradingPulseRingDelay} />
          <div className={styles.gradingIconInner}>
            <span className={styles.gradingIconEmoji}>📝</span>
          </div>
        </div>

        {/* Title */}
        <h3 className={styles.gradingTitle}>Grading Your Exam...</h3>

        {/* Description */}
        <p className={styles.gradingSubtitle}>
          Analyzing answers...
        </p>

        {/* Shimmer progress sweep bar */}
        <div className={styles.gradingProgressBarWrap}>
          <div className={styles.gradingProgressBarFill} />
        </div>

        {/* Animated jumping dots */}
        <div className={styles.gradingDotsRow}>
          <span className={styles.gradingDot} />
          <span className={styles.gradingDot} />
          <span className={styles.gradingDot} />
        </div>
      </div>
    </div>
  ) : null;

  const titleAuthModalJsx = showTitleAuthModal ? (
    <div
      className={`${styles.retakeModalOverlay} ${isExamFullscreen ? styles.retakeModalOverlayFullscreen : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="title-auth-modal-title"
    >
      <div className={`${styles.retakeModal} ${titleAuthError ? styles.pinShakeAnim : ''}`}>
        <div className={styles.retakeModalIcon}>🔒</div>
        <h3 id="title-auth-modal-title" className={styles.retakeModalTitle}>
          Edit Exam Title
        </h3>
        <p className={styles.retakeModalText}>
          Enter the exam password to save changes to the title. The session stays active for 5 minutes.
        </p>

        {/* 6-Digit PIN input in theme green */}
        <div className={styles.pinInputWrapper}>
          <div
            className={styles.pinBoxesRow}
            onClick={() => titleAuthInputRef.current?.focus()}
          >
            <input
              ref={titleAuthInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={titleAuthPassword}
              onChange={(e) => {
                setTitleAuthPassword(e.target.value.replace(/\D/g, ''));
                setTitleAuthError('');
              }}
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  titleAuthPassword.length === 6 &&
                  !titleAuthVerifying
                ) {
                  handleTitleAuthSubmit();
                }
                if (e.key === 'Escape') {
                  setShowTitleAuthModal(false);
                  setTitleAuthPassword('');
                  setTitleAuthError('');
                }
              }}
              className={styles.hiddenInputOverlay}
              autoFocus
            />

            {Array.from({ length: 6 }).map((_, idx) => {
              const isFilled = idx < titleAuthPassword.length;
              const isActive = idx === titleAuthPassword.length;
              const digit = titleAuthPassword[idx];

              return (
                <div
                  key={idx}
                  className={`${styles.pinBox} ${
                    isFilled ? styles.pinBoxFilled : ''
                  } ${isActive ? styles.pinBoxActive : ''} ${
                    titleAuthError ? styles.pinBoxError : ''
                  }`}
                >
                  {isFilled ? (
                    showTitleAuthEye ? (
                      <span
                        key={`digit-${idx}-${digit}`}
                        className={styles.pinDigit}
                      >
                        {digit}
                      </span>
                    ) : (
                      <span
                        key={`bullet-${idx}`}
                        className={styles.pinBullet}
                      />
                    )
                  ) : isActive ? (
                    <span className={styles.pinBlinkingCursor} />
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Borderless Eye toggle button */}
          <button
            type="button"
            className={styles.pinEyeBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowTitleAuthEye((prev) => !prev);
            }}
            title={showTitleAuthEye ? 'Hide password' : 'Show password'}
            aria-label={showTitleAuthEye ? 'Hide password' : 'Show password'}
          >
            {showTitleAuthEye ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        {titleAuthError && (
          <p className={styles.retakePwdError}>{titleAuthError}</p>
        )}

        <div className={styles.retakeModalActions}>
          <button
            className={styles.retakeModalCancel}
            onClick={() => {
              setShowTitleAuthModal(false);
              setTitleAuthPassword('');
              setTitleAuthError('');
            }}
            disabled={titleAuthVerifying}
          >
            Cancel
          </button>
          <button
            className={styles.retakeModalConfirm}
            onClick={handleTitleAuthSubmit}
            disabled={titleAuthVerifying || titleAuthPassword.length !== 6}
          >
            {titleAuthVerifying ? 'Verifying...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className={styles.page}>
      {result && (
        <div className={styles.scoreBanner} ref={scoreBannerRef}>
          <div className={styles.scoreBannerInner}>
            <div className={styles.scoreTrophy}>🏆</div>
            <div>
              <p className={styles.scoreTitle}>{result.isReview ? 'Review Mode' : 'Exam Completed!'}</p>
              <div className={styles.scoreNum}>
                <span className={styles.scoreBig}>{result.score}</span>
                <span className={styles.scoreOf}>/ {result.total}</span>
              </div>
            </div>
            <div className={styles.scoreActions}>
              <button className={styles.retakeBtn} onClick={handleRetake}>
                ↺ Retake
              </button>
              <button className={styles.backBtn} onClick={() => navigate('/study/spanish')}>
                ← Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Render modals outside fullscreen container (normal mode) */}
      {!isExamFullscreen && modalPortalTarget && retakeModalJsx && createPortal(retakeModalJsx, modalPortalTarget)}
      {!isExamFullscreen && modalPortalTarget && submitModalJsx && createPortal(submitModalJsx, modalPortalTarget)}
      {!isExamFullscreen && modalPortalTarget && gradingModalJsx && createPortal(gradingModalJsx, modalPortalTarget)}
      {!isExamFullscreen && modalPortalTarget && titleAuthModalJsx && createPortal(titleAuthModalJsx, modalPortalTarget)}

      <div className={styles.examPaperViewport} ref={examPaperRef}>
        {/* Render modals inside fullscreen container when in fullscreen mode */}
        {isExamFullscreen && retakeModalJsx}
        {isExamFullscreen && submitModalJsx}
        {isExamFullscreen && gradingModalJsx}
        {isExamFullscreen && titleAuthModalJsx}

        <header className={styles.examHeader}>
          <div className={styles.examHeaderRow}>
            <div className={styles.examTitleWrap}>
              <div className={styles.examTitleBlock}>
                {isEditingTitle ? (
                  <form
                    className={styles.examTitleEditForm}
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSaveTitle();
                    }}
                  >
                    <input
                      ref={titleInputRef}
                      type="text"
                      className={styles.examTitleInput}
                      value={editedTitle}
                      onChange={(e) => {
                        setEditedTitle(e.target.value);
                        if (titleSaveError) setTitleSaveError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          handleCancelEditTitle();
                        }
                      }}
                      disabled={isSavingTitle}
                      maxLength={120}
                    />
                    <div className={styles.examTitleEditActions}>
                      <button
                        type="submit"
                        className={styles.titleSaveBtn}
                        disabled={isSavingTitle || !editedTitle.trim()}
                        title="Save title"
                      >
                        {isSavingTitle ? (
                          'Saving...'
                        ) : (
                          <>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>Save</span>
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className={styles.titleCancelBtn}
                        onClick={handleCancelEditTitle}
                        disabled={isSavingTitle}
                        title="Cancel"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                        <span>Cancel</span>
                      </button>
                    </div>
                  </form>
                ) : (
                  <div
                    className={styles.examTitleDisplayWrap}
                    onClick={handleStartEditTitle}
                    title="Click to edit title"
                  >
                    <h1 className={styles.examTitle}>{exam.title}</h1>
                    <button
                      type="button"
                      className={styles.examTitleEditBtn}
                      aria-label="Edit exam title"
                      title="Edit exam title"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEditTitle();
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                  </div>
                )}
                {titleSaveError && <p className={styles.titleSaveError}>{titleSaveError}</p>}
                <p className={styles.examMeta}>{exam.subject.toUpperCase()} • {exam.level || 'Custom'}</p>
              </div>
            </div>
            <div className={styles.examHeaderActions}>
              <button
                type="button"
                className={styles.fullscreenBtn}
                onClick={toggleFullscreen}
              >
                {isExamFullscreen ? 'Close' : 'Full Screen'}
              </button>
              {!result && (
                <>
                  <div className={styles.timer}>
                    ⏱ {formatTime(timeElapsed)}
                  </div>
                  <button
                    className={`${styles.submitBtn}`}
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Exam'}
                  </button>
                </>
              )}
            </div>
          </div>
        </header>

        {renderExamLayout('page')}

        {!result && (
          <div className={styles.finalSubmit} style={{ paddingBottom: '30px' }}>
            <p className={styles.finalHint} style={{ paddingBottom: '10px', paddingTop: '10px' }}>
              You have answered {answeredCount} of {totalQuestions} questions.
            </p>
            <button
              className={styles.submitBtn}
              onClick={handleSubmit}
              disabled={submitting}
              type="button"
            >
              {submitting ? 'Submitting...' : 'Submit Exam'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
